<template>
  <section id="lm-datatable-builder">
    <h3>Data Table Builder</h3>
    <b-row>
      <LmTreeSelect
        v-if="!isOnEditMode"
        class="col-12"
        label="¿Qué datos desea listar?"
        pathToGetOptions="/datatables/constructor-options"
        v-model="builder.origin"
        @input="onBuilderOriginChange"
      />
      <template v-if="builder.origin">
        <LmTextInput
          class="col-12 col-md-4"
          label="Nombre del listado"
          v-model="$v.name.$model"
          :v="$v.name"
          name="name"
          @blur="validateSlug"
        />
        <LmTextInput
          class="col-12 col-md-4"
          label="Alias del listado"
          v-model="$v.alias.$model"
          :v="$v.alias"
          name="alias"
        />
        <LmNumberInputV2
          v-if="mode === 'list'"
          class="col-12 col-md-4"
          label="Resultados por página"
          v-model="size"
          :v="$v.size"
        />
        <LmNumberInputV2
          v-if="mode === 'box'"
          class="col-12 col-md-4"
          label="Filas por página"
          v-model="rows"
          @input="onRowsPerPageChange"
        />
        <LmTextInput
          class="col-12 col-md-4"
          label="Slug"
          v-model="$v.locationSlug.$model"
          :v="$v.locationSlug"
          name="locationSlug"
        />
        <LmTextInput
          v-if="isOnEditMode"
          class="col-12 col-md-4"
          label="Origen de Datos"
          :disabled="true"
          v-model="builder.origin.name"
          name="builderOriginName"
        />
        <LmColorPicker
          v-if="mode === 'box'"
          class="col-12 col-md-4"
          label="Color de énfasis"
          :swatches="swatches"
          v-model="tableAccentColor"
        />
        <div class="col-12 d-flex mb-4" style="gap: 1rem">
          <b-form-checkbox
            v-if="showBoxModeCheckbox"
            v-model="mode"
            value="box"
            switch
            unchecked-value="list"
            @input="onModeChange"
            class="py-2"
          >
            Modo caja
          </b-form-checkbox>
          <b-form-checkbox
            v-model="selectable"
            :disabled="mode !== 'list'"
            switch
            class="py-2"
          >
            <span class="text-nowrap">
              Seleccionble
            </span>
          </b-form-checkbox>
          <LmSelect
            v-if="selectable"
            v-model="selectMode"
            :options="{
              content: [
                { text: 'Múltiple', value: 'multi' },
                { text: 'Simple', value: 'single' },
              ]
            }"
          />
          <b-form-checkbox
            v-if="enableMultipleDocumentSignFeature && selectable && builder.origin.id === 'documents'"
            v-model="showMultipleSignButton"
            switch
            class="py-2"
          >
            <span class="text-nowrap">
              Mostrar botón de firma
            </span>
          </b-form-checkbox>
          <b-form-checkbox
            v-model="showCancelButton"
            switch
            class="py-2"
          >
            <span class="text-nowrap">
              Mostrar botón cancelar
            </span>
          </b-form-checkbox>
          <b-form-checkbox
            v-if="mode !== 'box'"
            v-model="enableCsvExport"
            switch
            class="py-2"
          >
            <span class="text-nowrap">
              Permitir exportar CSV
            </span>
          </b-form-checkbox>
          <b-form-checkbox
            v-if="showGeoSearchCheckbox"
            v-model="enableGeoSearch"
            switch
            class="py-2"
          >
            <span class="text-nowrap">
              Permitir búsqueda por geolocalización
            </span>
          </b-form-checkbox>
          <b-form-checkbox
            v-if="showCustomSearchCheckbox"
            v-model="enableCustomSearch"
            switch
            class="py-2"
          >
            <span class="text-nowrap">
              Permitir búsqueda personalizada
            </span>
          </b-form-checkbox>
          <b-form-checkbox
            v-if="showCalendarModeCheckbox"
            v-model="mode"
            value="calendar"
            switch
            unchecked-value="list"
            @input="onModeChange"
            class="py-2"
          >
            Modo calendario
          </b-form-checkbox>
        </div>
      </template>
    </b-row>

    <template v-if="!isLoading">
      <section class="bg-light p-4 mb-4 rounded" v-if="builder.fields.length">
        <!-- list mode -->
        <template v-if="mode === 'list'">
          <div class="d-flex justify-content-between">
            <h4>Columnas</h4>
            <dropdown-menu
              v-model="showColumnsDropdown"
              interactive
              right
            >
              <b-button variant="info" size="lg">Columnas</b-button>
              <div slot="dropdown">
                <div class="px-4 py-2 dropdown-content scroll">
                  <b-form-checkbox
                    class="py-1"
                    switch
                    v-for="(item, index) in builder.fields"
                    :key="index"
                    v-model="item.visible"
                  >
                    <span class="text-nowrap">{{ item.label }}</span>
                  </b-form-checkbox>
                </div>
              </div>
            </dropdown-menu>
          </div>
          <draggable
            tag="div"
            class="row scroll align-items-stretch"
            v-model="visibleFields"
            animation="150"
            handle=".handle"
          >
            <b-col
              v-for="(item, index) in visibleFields" :key="index"
              cols="3"
              class="py-4 handle"
            >
              <div class="rounded shadow p-4 h-100" :class="{'border border-primary': item.primaryColumn}">
                <div class="d-flex align-items-center justify-content-between">
                  <h6 :id="item.key">{{ item.label }}</h6>
                  <b-dropdown size="lg" variant="link" toggle-class="text-decoration-none column-label-edit-button text-secondary" no-caret right>
                    <template #button-content>
                      <i class="mdi mdi-tune"></i>
                      <span class="sr-only">Edit</span>
                    </template>
                    <div class="p-2 column-label-edit-dropdown">
                      <LmTextInput v-model="item.label" class="mb-0" label="Título de la columna" />
                      <b-form-checkbox
                        v-if="showSortableCheckbox(item)"
                        class="py-2"
                        switch
                        v-model="item.sortable"
                      >
                        <span class="text-nowrap">Columna ordenable</span>
                      </b-form-checkbox>
                    </div>
                  </b-dropdown>
                </div>
                <hr class="w-100" />

                <p v-if="item.stickyColumn" class="text-primary">
                  <i class="mdi mdi-star-outline info-icon"></i>
                  Columna primaria
                </p>
                <p v-if="item.description">
                  <i class="mdi mdi-information-outline info-icon"></i>
                  {{ item.description }}
                </p>
                <p v-if="item.sortable">
                  <i class="mdi mdi-sort-alphabetical-ascending info-icon"></i>
                  Esta columna se puede ordenar
                </p>

                <div v-if="item.render === 'actions'">
                  <p>Opciones</p>
                  <b-form-checkbox
                    class="py-2"
                    switch
                    v-for="(item, index) in item.options"
                    :key="index"
                    v-model="item.visible"
                  >
                    <span class="text-nowrap">{{ item.label }}</span>
                  </b-form-checkbox>
                </div>
              </div>
            </b-col>
          </draggable>
        </template>
        <!-- box mode -->
        <template v-else-if="mode === 'box'">
          <h4>Cajas</h4>
          <div class="d-flex justify-content-center box-preview">
            <b-card class="w-75" :style="`border-top: 4px solid ${tableAccentColor}`">
              <p v-if="primaryField.description">
                <i class="mdi mdi-information-outline info-icon"></i>
                {{ primaryField.description }}
              </p>
              <LmSelect
                label="Tamaño de la caja"
                :options="boxModeColsOptions"
                v-model="primaryField.boxCols"
                @input="onBoxColsChange"
              />
              <template v-if="primaryField.hasOwnProperty('boxOptions')">
                <b-row class="align-items-center">
                  <LmSelect
                    class="col-auto"
                    label="Título de la caja"
                    v-model="primaryField.boxOptions.boxTitleKey"
                    :options="boxModeTitleAndContentOptions"
                  />
                  <LmNumberInputV2
                    class="col-auto"
                    label="Lineas máximas"
                    v-model="primaryField.boxOptions.boxTitleLines"
                    :disabled="primaryField.boxOptions.boxTitleKey === 'NULL'"
                  />
                </b-row>
                <b-row class="align-items-center">
                  <LmSelect
                    class="col-auto"
                    label="Descripción de la caja"
                    v-model="primaryField.boxOptions.boxContentKey"
                    :options="boxModeTitleAndContentOptions"
                  />
                  <LmNumberInputV2
                    class="col-auto"
                    label="Lineas máximas"
                    v-model="primaryField.boxOptions.boxContentLines"
                    :disabled="primaryField.boxOptions.boxContentKey === 'NULL'"
                  />
                </b-row>
              </template>
              <p v-if="primaryField.boxOptions.boxContentKey === 'NULL' && primaryField.boxOptions.boxTitleKey === 'NULL'" class="text-danger">
                Selecciona al menos una propiedad a mostrar en la caja
              </p>
            </b-card>
          </div>
        </template>
      </section>

      <b-tabs content-class="bg-light p-4 mb-4" v-if="builder.fields.length">
        <b-tab title-item-class="bg-light" title="Básico" active>
          <div class="d-flex justify-content-between">
            <h4>Filtros por defecto</h4>
            <dropdown-menu
              v-model="showClientFiltersDropdown"
              interactive
              right
            >
              <b-button variant="info" size="lg">Filtros del cliente</b-button>
              <div slot="dropdown">
                <div class="px-4 py-2 dropdown-content scroll">
                  <b-form-checkbox
                    class="py-2"
                    switch
                    v-for="(item, index) in clientFilters"
                    :key="index"
                    v-model="item.visible"
                  >
                    <span class="text-nowrap">{{ item.label }}</span>
                  </b-form-checkbox>
                </div>
              </div>
            </dropdown-menu>
          </div>
          <div class="d-flex align-items-center flex-wrap mt-4" style="gap: 1rem">
            <div
              v-for="(item, index) in constructorFilters"
              :key="index"
            >
              <Component
                :key="item.elastic_key"
                :is="defineComponent(item.type)"
                v-bind="item"
                :showToggleAll="item.showToggleAll"
                @change="onFilterChange(item, $event)"
              />
            </div>
          </div>
        </b-tab>
        <b-tab title-item-class="bg-light">
          <template #title>
            Avanzado <b-icon class="ml-2" v-if="!emptyQueryString" variant="info" icon="eye"></b-icon>
          </template>
          <h4>Filtros avanzados</h4>
          <LmCodeInput
            class="mb-4"
            v-model="builder.queryString"
          />
          <LmButton
            :disabled="emptyQueryString"
            value="Validar consulta"
            :isProcessing="isValidatingQs"
            @click="validateQs"
          />
          <div v-if="!emptyQueryString && Object.keys(qsValidationMessage).length">
            <div v-if="qsValidationMessage.status">
              <p class="text-success">Sintaxis de la consulta correcta. Se encontrarón {{ qsValidationMessage.count }} resultados.</p>
            </div>
            <div v-else>
              <p class="text-danger" v-b-toggle.error-detail>Error: {{ qsValidationMessage.message }}</p>
              <b-collapse id="error-detail" class="mt-2">
                <b-card class="w-100">
                  <pre class="card-text">{{ qsValidationMessage.detail }}</pre>
                </b-card>
              </b-collapse>
            </div>
          </div>
        </b-tab>
      </b-tabs>
      <!-- Ordenamiento -->
      <section class="bg-light p-4 mb-4 rounded h-32" v-if="sortableColumns.length">
        <h4>Ordenamiento</h4>
        <div class="d-flex justify-content-start ">
         <Ordering
          ref="ordering"
          v-if="sortableColumns.length"
          :columns="sortableColumns"
          :defaultValue="builder.sort"
          @change="onSortChange"
        />
        </div>
      </section>
    </template>

    <!-- loading state -->
    <template v-else>
      <b-row class="mb-4">
        <b-col v-for="item in 5" :key="item">
          <b-skeleton-img height="250px"></b-skeleton-img>
        </b-col>
      </b-row>
      <b-row class="mb-4">
        <b-col cols="auto" v-for="item in 2" :key="item">
          <b-skeleton type="button"></b-skeleton>
        </b-col>
      </b-row>
    </template>

    <template>
      <a-modal
        v-model="showPreview"
        :title="$t('preview')"
        :footer="null"
        width="100%"
        :destroyOnClose="true"
      >
        <div>
          <LmDataTable
            ref="datatable_preview"
            :isOnPreviewMode="true"
            :slug="locationSlug"
            :previewData="previewData"
          />
        </div>
      </a-modal>
    </template>
    <div class="buttons">
      <LmButton
          buttonClass="btn-secondary"
          :value="$t('cancel')"
          @click="onCancel"
        />
        <LmButton
          v-if="!deleted"
          :disabled="disableSaveButton"
          buttonClass="btn-primary"
          :isProcessing="isProcessing"
          :value="$t('save')"
          @click="onSave"
        />
        <LmButton
          v-if="!deleted && isOnEditMode && upgradeAvailable"
          buttonClass="btn-primary"
          :isProcessing="isProcessing"
          :value="$t('update_literal')"
          @click="onUpgrade"
          requireConfirmation
          confirmationTitle="¿Estás seguro de actualizar la tabla?"
          :confirmationMessage="upgradeConfirmationMessage"
        />
        <LmButton
          v-if="!deleted"
          buttonClass="btn-danger"
          :isProcessing="isProcessing"
          action="delete"
          :value="$t('delete')"
          confirmationTitle="¿Quiéres eliminar esta table de datos?"
          @click="onDelete"
        />
        <LmButton
          v-if="deleted"
          buttonClass="btn-info"
          :isProcessing="isProcessing"
          :value="$t('reactivate')"
          @click="onReactivate"
        />
        <LmButton
          v-if="isOnEditMode"
          buttonClass="btn-warning"
          :value="$t('preview')"
          @click="onShowPreview"
        />
        <LmButton
          v-if="isOnEditMode"
          buttonClass="btn-success"
          :value="$t('goto_list')"
          requireConfirmation
          confirmationTitle="Para ver los cambios recordá guardar el listado."
          @click="onView"
        />
    </div>
  </section>
</template>

<script>
// Components
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmColorPicker from '@/components/form/elements/LmColorPicker'
import LmSelect from '@/components/form/elements/LmSelect'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmNumberInputV2 from '@/components/form/elements/LmNumberInputV2'
import LmCodeInput from '@/components/form/elements/LmCodeInput'
import LmButton from '@/components/form/elements/LmButton'
import FilterSelect from './components/FilterSelect'
import FilterSelectAggregate from './components/FilterSelectAggregate'
import FilterDate from './components/FilterDate'
import DropdownMenu from '@innologica/vue-dropdown-menu'
import Ordering from './components/Ordering'
// Solo para el preview
import LmDataTable from '@/components/lmDataTable/table'

// Vuex
import { mapGetters } from 'vuex'

// Utils
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts'
import draggable from 'vuedraggable'
import { required, maxValue } from 'vuelidate/lib/validators'

export default {
  mixins: [lmToasts],
  props: {
    isOnEditMode: {
      type: Boolean,
      required: true
    },
    slug: {
      type: String,
      default: null
    }
  },
  components: {
    LmSelect,
    LmTreeSelect,
    LmColorPicker,
    LmTextInput,
    LmNumberInputV2,
    LmCodeInput,
    LmButton,
    FilterSelect,
    FilterDate,
    FilterSelectAggregate,
    draggable,
    DropdownMenu,
    LmDataTable,
    Ordering
  },
  data () {
    return {
      isLoading: false,
      isValidatingQs: false,
      isProcessing: false,
      upgradeAvailable: false,
      upgradeDetails: null,
      showColumnsDropdown: false,
      showClientFiltersDropdown: false,
      tableAccentColor: '',
      qsValidationMessage: {},
      id: '',
      name: '',
      alias: '',
      locationSlug: '',
      size: 10,
      rows: 4,
      mode: 'list',
      selectable: false,
      selectMode: 'multi',
      enableCsvExport: false,
      enableGeoSearch: false,
      enableCustomSearch: false,
      showMultipleSignButton: false,
      showCancelButton: false,
      deleted: false,
      builder: {
        origin: null,
        fields: [],
        filters: [],
        buttons: [],
        queryString: null,
        sort: [],
        geoSearcher: {},
        customSearchers: [],
        calendarModeProp: {}
      },
      visibleFields: [],
      showPreview: false,
      sortRulesToApply: [],
      sortableColumns: []
    }
  },
  validations: {
    name: { required },
    alias: { required },
    locationSlug: { required },
    size: { required, maxValue: maxValue(50) }
  },
  computed: {
    ...mapGetters('settings', ['accentColor']),
    // sortableColumns () {
    //   return this.visibleFields.filter(el => el?.sortable) || []
    // },
    swatches () {
      return [
        ['#b2dfdb', '#4db6ac', '#009688', '#00796b'],
        ['#ffe0b2', '#ffb74d', '#f57c00', '#f57c00'],
        ['#e1bee7', '#ba68c8', '#9c27b0', '#6a1b9a'],
        ['#90caf9', '#42a5f5', '#1e88e5', '#1565c0'],
        [this.accentColor]
      ]
    },
    primaryField () {
      return this.builder.fields.find(field => field.primaryColumn)
    },
    boxModeColsOptions () {
      return {
        content: [
          { text: '1/4', value: 3 },
          { text: '1/3', value: 4 },
          { text: '1/2', value: 6 }
        ]
      }
    },
    boxModeTitleAndContentOptions () {
      // Utilizamos solo las columnas con un valor simple y que se encuentren como propiedad de elastic
      // en la columna primaria. Nota. Debe existir la propiedad de elastic en la columna primaria y como columna de valor simple
      const fields = this.builder.fields.filter(el => {
        return el.render === 'value' && Object.keys(this.primaryField.elastic_key).includes(Object.keys(el.elastic_key).at(0))
      })
      const options = {
        content: fields.map(el => {
          return {
            text: el.label,
            value: Object.values(el.elastic_key).at(0)
          }
        })
      }
      options.content.unshift({
        text: 'Ninguno',
        value: 'NULL'
      })
      return options
    },
    constructorFilters () {
      return this.builder.filters.filter(el => !el.hideOnConstructor)
    },
    clientFilters () {
      return this.builder.filters.filter(el => !el.hideOnClient)
    },
    upgradeConfirmationMessage () {
      let message = ''
      if (this.upgradeAvailable && this.upgradeDetails) {
        for (const key in this.upgradeDetails) {
          if (this.upgradeDetails[key].length) {
            message += this.upgradeDetailMessageItem(key)
            message += '<ul style="padding-left: 20px">'
            for (const item of this.upgradeDetails[key]) {
              message += `<li style="list-style: initial">${item.label}</li>`
            }
            message += '</ul>'
          }
        }
      }
      return message
    },
    disableSaveButton () {
      if (
        this.mode === 'box' &&
        this.primaryField.boxOptions.boxTitleKey === 'NULL' &&
        this.primaryField.boxOptions.boxContentKey === 'NULL'
      ) {
        return true
      }
      return false
    },
    showBoxModeCheckbox () {
      if (this.primaryField?.hasOwnProperty('boxOptions')) {
        return true
      }
      return false
    },
    showCalendarModeCheckbox () {
      if (typeof this.builder.calendarModeProp === 'object' && Object.keys(this.builder.calendarModeProp).length > 0) {
        return true
      }
      return false
    },
    showCustomSearchCheckbox () {
      if (this.mode === 'box' && this.builder.fields.some(field => field.render === 'code')) {
        return true
      }
      return false
    },
    showGeoSearchCheckbox () {
      if (this.mode !== 'box' && typeof this.builder.geoSearcher === 'object' && Object.keys(this.builder.geoSearcher).length > 0) {
        return true
      }
      return false
    },
    enableMultipleDocumentSignFeature () {
      return process.env?.VUE_APP_ENABLE_MULTIPLE_DOCUMENT_SIGN === 'yes'
    },
    emptyQueryString () {
      return this.builder.queryString === null || this.builder.queryString?.trim() === ''
    },
    previewData () {
      return {
        name: this.name,
        alias: this.alias,
        slug: this.locationSlug,
        size: this.size,
        mode: this.mode,
        selectable: this.selectable,
        selectMode: this.selectMode,
        filtersToApply: this.builder.filters,
        table: {
          ...this.builder,
          filters: this.builder.filters.filter(item => item.visible && !item.hideOnClient),
          fields: [].concat(this.visibleFields, this.builder.fields.filter(item => !item.visible))
        },
        accentColor: this.tableAccentColor,
        enableCsvExport: this.enableCsvExport,
        enableGeoSearch: this.enableGeoSearch,
        enableCustomSearch: this.enableCustomSearch,
        showMultipleSignButton: this.showMultipleSignButton,
        showCancelButton: this.showCancelButton
      }
    }
  },
  methods: {
    async onSortChange (event) {
      this.builder.sort = event
    },
    async validateQs () {
      this.isValidatingQs = true
      try {
        const res = await commonsApi.axiosPostWrapper('/datatables/validate-qs', {
          qs: this.builder.queryString,
          origin: this.builder.origin.id
        })
        this.qsValidationMessage = {
          ...res.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isValidatingQs = false
      }
    },
    validateForm (validations) {
      return new Promise((resolve, reject) => {
        let error = false
        validations.$touch()
        error = validations.$error
        if (error) {
          for (const key in this) {
            if (validations.hasOwnProperty(key)) {
              if (validations[key].$error) {
                const selector = this.$el.querySelector(`[name="${key}"]`)
                selector.scrollIntoView({ behavior: 'smooth' })
                break
              }
            }
          }
          this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error de validación')
          reject(new Error('validation error'))
        } else {
          resolve(true)
        }
      })
    },
    upgradeDetailMessageItem (key) {
      let action = ''
      let type = ''

      if ((/modified/).test(key)) {
        action = 'modificará'
      }
      if ((/added/).test(key)) {
        action = 'modificará'
      }
      if ((/removed/).test(key)) {
        action = 'modificará'
      }
      if ((/Fields/).test(key)) {
        type = 'columna/s'
      }
      if ((/Filters/).test(key)) {
        type = 'filtro/s'
      }

      return `<p>Se ${action} ${this.upgradeDetails[key].length} ${type}</p>`
    },
    defineComponent (type) {
      const components = {
        selector: 'FilterSelect',
        'time-ago': 'FilterSelect',
        'time-from-now': 'FilterSelect',
        'date-range': 'FilterDate',
        'selector-aggregate': 'FilterSelectAggregate'
      }
      return components[type]
    },
    onFilterChange (item, event) {
      item.pick = event
    },
    onCancel () {
      this.$router.go(-1)
    },
    async onSave () {
      try {
        this.isProcessing = true
        await this.validateForm(this.$v)

        this.builder.fields = [].concat(this.visibleFields, this.builder.fields.filter(item => !item.visible))

        const builderData = {
          name: this.name,
          alias: this.alias,
          slug: this.locationSlug,
          size: this.size,
          mode: this.mode,
          selectable: this.selectable,
          selectMode: this.selectMode,
          builder: this.builder,
          accentColor: this.tableAccentColor,
          enableCsvExport: this.enableCsvExport,
          enableGeoSearch: this.enableGeoSearch,
          enableCustomSearch: this.enableCustomSearch,
          showMultipleSignButton: this.showMultipleSignButton,
          showCancelButton: this.showCancelButton
        }
        let res = null
        if (this.isOnEditMode) {
          res = await commonsApi.axiosPutWrapper(`/datatables/${this.id}`, builderData)
        } else {
          res = await commonsApi.axiosPostWrapper('/datatables', builderData)
        }
        this.lmToastSuccessInternalLink(
          `/listado/${res.data.slug}`,
          `Se guardó la tabla ${this.name} corectamente, échale un vistazo`,
          'Acción realizada con éxito'
        )
        if (!this.isOnEditMode) {
          this.$router.replace(`/datatable/editar/${res.data.slug}`)
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },
    async onUpgrade () {
      try {
        await commonsApi.axiosPutWrapper('/datatables/upgrade/' + this.id)
        this.$router.go()
      } catch (error) {
        console.log(error)
      }
    },
    async onDelete () {
      try {
        this.isProcessing = true
        await commonsApi.axiosDeleteWrapper(`/datatables/delete/${this.id}`)
        this.lmToastSuccessMessage(
          `Se eliminó la tabla de datos correctamente.`,
          'Acción realizada con éxito'
        )
        this.deleted = true
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },
    async onReactivate () {
      try {
        this.isProcessing = true
        await commonsApi.axiosPutWrapper(`/datatables/reactivate/${this.id}`)
        this.lmToastSuccessMessage(
          `Se activó la tabla de datos correctamente.`,
          'Acción realizada con éxito'
        )
        this.deleted = false
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },
    async onBuilderOriginChange (event) {
      try {
        this.isLoading = true
        const res = await commonsApi.axiosGetWrapper(`/datatables/${event.id}/constructor`)
        this.builder.fields = res.data.fields
        this.builder.filters = res.data.filters
        this.builder.buttons = res.data.buttons
        this.builder.sort = res.data.sort
        this.builder.geoSearcher = res.data.geoSearcher
        this.builder.customSearchers = res.data.customSearchers
        this.builder.calendarModeProp = res.data.calendarModeProp
        this.sortableColumns = this.builder.fields.filter(el => el?.sortable && el.visible) || []
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    onRowsPerPageChange (event) {
      const rows = event
      const box = this.primaryField.boxCols
      const width = 12
      this.size = (width / box) * rows
    },
    onBoxColsChange () {
      this.onRowsPerPageChange(this.rows)
    },
    onModeChange () {
      this.selectable = false
      if (!this.tableAccentColor) {
        this.tableAccentColor = this.accentColor
      }
      this.onRowsPerPageChange(this.rows)
    },
    async validateSlug () {
      const res = await commonsApi.axiosPostWrapper('/datatables/validate-slug', {
        id: this.id,
        slug: this.locationSlug
      })
      this.locationSlug = res.data
    },
    showSortableCheckbox (item) {
      return item.hasOwnProperty('sortable')
    },
    onView () {
      this.$router.push(`/listado/${this.slug}`)
    },
    onShowPreview () {
      this.showPreview = true
    }
  },
  watch: {
    'builder.fields': {
      immediate: true,
      deep: true,
      handler (val) {
        const visibles = val.filter(item => item.visible)

        if (!this.visibleFields.length) {
          this.visibleFields = visibles
        } else {
          const arr = []
          let ind = this.visibleFields.length

          for (const item of visibles) {
            const index = this.visibleFields.findIndex(el => el.key === item.key)

            if (index > -1) {
              arr[index] = this.visibleFields[index]
              continue
            }

            while (arr[ind] !== undefined) {
              ind++
            }
            arr[ind] = item
          }

          this.visibleFields = arr.filter(item => item)
        }
      }
    },
    'builder.queryString': {
      immediate: true,
      handler (val) {
        this.qsValidationMessage = {}
      }
    }
  },
  async created () {
    try {
      this.isLoading = true
      if (this.isOnEditMode) {
        const res = await commonsApi.axiosGetWrapper(`/datatables/${this.slug}/builder`)
        this.id = res.data._id
        this.name = res.data.name
        this.locationSlug = res.data.slug
        this.alias = res.data.alias
        this.size = res.data.size
        this.selectable = res.data.selectable
        this.mode = res.data.mode
        this.builder.fields = res.data.builder.fields
        this.builder.filters = res.data.builder.filters
        this.builder.buttons = res.data.builder.buttons

        // this.visibleFields = this.builder.fields.filter(item => item.visible)
        this.sortableColumns = this.builder.fields.filter(el => el?.sortable && el.visible) || []
        this.builder.sort = res.data.builder.sort

        this.builder.geoSearcher = res.data.builder.geoSearcher
        this.builder.customSearchers = res.data.builder.customSearchers
        this.builder.calendarModeProp = res.data.builder.calendarModeProp
        this.builder.origin = res.data.builder.origin
        this.builder.queryString = res.data.builder.queryString || null
        this.deleted = res.data.deleted
        this.tableAccentColor = res.data.accentColor || this.accentColor || ''
        this.enableCsvExport = res.data.enableCsvExport
        this.enableGeoSearch = res.data.enableGeoSearch
        this.enableCustomSearch = res.data.enableCustomSearch
        this.showMultipleSignButton = res.data.showMultipleSignButton
        this.showCancelButton = res.data.showCancelButton

        const response = await commonsApi.axiosGetWrapper('/datatables/check-for-upgrade/' + this.id)
        this.upgradeAvailable = response.data.hasUpgrade || false
        this.upgradeDetails = response.data.details
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
    this.$watch('name', val => {
      this.locationSlug = val.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '')
    })
  }
}
</script>

<style>
#lm-datatable-builder .box-preview {
  padding: 2rem 0;
}

#lm-datatable-builder .info-icon {
  font-size: 1rem;
}

#lm-datatable-builder .column-label-edit-dropdown {
  width: 200px;
}

#lm-datatable-builder .btn.column-label-edit-button {
  font-size: 1.2rem;
}

#lm-datatable-builder .btn.column-label-edit-button:focus {
  box-shadow: none;
  outline: none;
}

#lm-datatable-builder .dropdown-content {
  max-height: 350px;
  overflow-y: auto;
}

#lm-datatable-builder .scroll {
  flex-wrap: nowrap;
  overflow-x: auto;
  padding: 2rem 0;
}

#lm-datatable-builder .scroll:hover::-webkit-scrollbar-thumb {
  border-radius: 20px;
  background: #888;
}

#lm-datatable-builder .scroll::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

#lm-datatable-builder .scroll::-webkit-scrollbar-thumb {
  border-radius: 20px;
  background: #888;
}

#lm-datatable-builder .scroll::-webkit-scrollbar-thumb:hover {
  background: #555;
}
  </style>
